import { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Input,
  UniversalLoadingState,
  Card,
  FormField,
  NotificationCard,
  useToast,
} from '@afrilink/ui';
import {
  billingInfoService,
  type BillingInfo,
} from '@/domains/customers/services/billingInfoService';

export default function SettingsBillingInfoPage() {
  const { toast } = useToast();
  const [data, setData] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ccText, setCcText] = useState('');

  useEffect(() => {
    let cancelled = false;
    billingInfoService
      .get()
      .then((bi) => {
        if (cancelled) return;
        setData(bi);
        setCcText(bi.billingEmailCc.join(', '));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load billing info.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function setField<K extends keyof BillingInfo>(key: K, value: BillingInfo[K]) {
    setData((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;

    setError(null);
    setSaving(true);

    const ccList = ccText
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes('@'));

    try {
      const updated = await billingInfoService.update({ ...data, billingEmailCc: ccList });
      setData(updated);
      setCcText(updated.billingEmailCc.join(', '));
      toast({ tone: 'success', title: 'Billing info saved' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not save.';
      setError(msg);
      toast({ tone: 'danger', title: 'Save failed', body: msg });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card interactive={false} className="p-12">
        <UniversalLoadingState variant="spinner" caption="Loading billing info…" />
      </Card>
    );
  }

  if (!data) {
    return <NotificationCard tone="danger" title="Could not load billing info" body={error ?? 'Unknown error.'} />;
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <Section title="Contact" description="Used as the default account-holder name on invoices and the address we email to.">
        <FormField label="Full name" htmlFor="full_name" required>
          <Input
            id="full_name"
            value={data.fullName}
            onChange={(e) => setField('fullName', e.target.value)}
            disabled={saving}
            autoComplete="name"
          />
        </FormField>
        <FormField label="Email" htmlFor="email" helper="Linked to your sign-in account. Contact support to change.">
          <Input id="email" value={data.email} disabled readOnly />
        </FormField>
        <FormField label="Phone" htmlFor="phone">
          <Input
            id="phone"
            value={data.phone}
            onChange={(e) => setField('phone', e.target.value)}
            disabled={saving}
            autoComplete="tel"
            placeholder="+27 76 700 4882"
          />
        </FormField>
      </Section>

      <Section title="Company" description="Optional. Printed on the invoice if you are a registered business.">
        <FormField label="Company name" htmlFor="company">
          <Input
            id="company"
            value={data.company}
            onChange={(e) => setField('company', e.target.value)}
            disabled={saving}
            autoComplete="organization"
            placeholder="My Business (Pty) Ltd"
          />
        </FormField>
        <FormField
          label="Registration number"
          htmlFor="reg"
          helper="SA company registration (CIPC)"
        >
          <Input
            id="reg"
            value={data.registrationNumber}
            onChange={(e) => setField('registrationNumber', e.target.value)}
            disabled={saving}
            placeholder="2024/123456/07"
          />
        </FormField>
        <FormField
          label="VAT number"
          htmlFor="vat"
          helper="Optional. Printed on the invoice for your records. SightHost does not charge VAT."
        >
          <Input
            id="vat"
            value={data.vatNumber}
            onChange={(e) => setField('vatNumber', e.target.value)}
            disabled={saving}
            placeholder="4XXXXXXXXX"
          />
        </FormField>
      </Section>

      <Section title="Billing address" description="Printed in the 'Bill To' block on every invoice.">
        <FormField label="Address line 1" htmlFor="addr1">
          <Input
            id="addr1"
            value={data.billingAddressLine1}
            onChange={(e) => setField('billingAddressLine1', e.target.value)}
            disabled={saving}
            autoComplete="address-line1"
          />
        </FormField>
        <FormField label="Address line 2" htmlFor="addr2">
          <Input
            id="addr2"
            value={data.billingAddressLine2}
            onChange={(e) => setField('billingAddressLine2', e.target.value)}
            disabled={saving}
            autoComplete="address-line2"
          />
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="City" htmlFor="city">
            <Input
              id="city"
              value={data.billingAddressCity}
              onChange={(e) => setField('billingAddressCity', e.target.value)}
              disabled={saving}
              autoComplete="address-level2"
            />
          </FormField>
          <FormField label="Postal code" htmlFor="postal">
            <Input
              id="postal"
              value={data.billingAddressPostalCode}
              onChange={(e) => setField('billingAddressPostalCode', e.target.value)}
              disabled={saving}
              autoComplete="postal-code"
            />
          </FormField>
        </div>
        <FormField label="Country" htmlFor="country">
          <Input
            id="country"
            value={data.billingAddressCountry}
            onChange={(e) => setField('billingAddressCountry', e.target.value.toUpperCase().slice(0, 2))}
            disabled={saving}
            autoComplete="country"
            placeholder="ZA"
            maxLength={2}
          />
        </FormField>
      </Section>

      <Section
        title="Invoice email"
        description="Where we send invoices and payment receipts. Defaults to your sign-in email if blank."
      >
        <FormField
          label="Send invoices to"
          htmlFor="bemail"
          helper="Leave blank to use your sign-in email."
        >
          <Input
            id="bemail"
            type="email"
            value={data.billingEmail}
            onChange={(e) => setField('billingEmail', e.target.value)}
            disabled={saving}
            placeholder={data.email}
          />
        </FormField>
        <FormField
          label="CC"
          htmlFor="cc"
          helper="Comma-separated. Useful for sending invoices to your accountant."
        >
          <Input
            id="cc"
            value={ccText}
            onChange={(e) => setCcText(e.target.value)}
            disabled={saving}
            placeholder="accountant@example.com, finance@mybusiness.co.za"
          />
        </FormField>
      </Section>

      {error && <NotificationCard tone="danger" title="Save failed" body={error} />}

      <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-border bg-card px-6 py-3">
        <Button type="submit" disabled={saving} loading={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card interactive={false}>
      <header className="border-b border-border px-6 py-3">
        <h3 className="text-sm font-semibold text-navy-primary">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </header>
      <div className="space-y-4 px-6 py-5">{children}</div>
    </Card>
  );
}
